import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ApiError } from '@/utils/ApiError';
import type { ApiResponse, ErrorResponse, PagedResponse } from '@/types/api';
import { tokenStorage } from './tokenStorage';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/** Endpoints that must never trigger a refresh attempt — they are how refreshing works. */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh', '/auth/logout'];

/** Notifies the app that the session ended and the user must sign in again. */
type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler = () => {};

export function setSessionExpiredHandler(handler: SessionExpiredHandler): void {
  onSessionExpired = handler;
}

export const http: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// ---------------------------------------------------------------- request

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token && !isAuthEndpoint(config.url)) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --------------------------------------------------------------- response

/*
 * Single-flight refresh.
 *
 * A dashboard fires several requests at once, so an expired access token
 * produces several simultaneous 401s. Refreshing per failure would send N
 * concurrent refresh calls with the same token — and because refresh tokens
 * rotate, the first would succeed and the rest would look like token reuse,
 * which the server correctly treats as a compromise and answers by revoking
 * every session. The user would be logged out precisely because their app was
 * behaving normally.
 *
 * So the first 401 starts one refresh; the others await that same promise and
 * then retry with whatever it produced.
 */
let refreshInFlight: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new ApiError({ code: 'UNAUTHENTICATED', message: 'No session', status: 401 });
  }

  // A bare axios call: the shared instance would recurse through this same
  // interceptor on failure.
  const response = await axios.post<ApiResponse<{ accessToken: string; refreshToken: string }>>(
    `${BASE_URL}/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const pair = response.data.data;
  tokenStorage.save({ accessToken: pair.accessToken, refreshToken: pair.refreshToken });
  return pair.accessToken;
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ErrorResponse>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;

    const shouldAttemptRefresh =
      error.response?.status === 401 &&
      original !== undefined &&
      original._retried !== true &&
      !isAuthEndpoint(original.url);

    if (shouldAttemptRefresh && original) {
      original._retried = true;
      try {
        refreshInFlight ??= refreshAccessToken().finally(() => {
          refreshInFlight = null;
        });
        const accessToken = await refreshInFlight;
        original.headers.Authorization = `Bearer ${accessToken}`;
        return http(original);
      } catch {
        // The refresh token is spent, revoked or expired. This is a real
        // session expiry, not a transient failure.
        tokenStorage.clear();
        onSessionExpired();
        throw new ApiError({
          code: 'TOKEN_EXPIRED',
          message: 'Your session has expired. Please sign in again.',
          status: 401,
        });
      }
    }

    throw toApiError(error);
  },
);

function isAuthEndpoint(url?: string): boolean {
  return url !== undefined && AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

function toApiError(error: AxiosError<ErrorResponse>): ApiError {
  const body = error.response?.data;
  // A well-formed error envelope from our own API...
  if (body && typeof body === 'object' && 'code' in body && 'message' in body) {
    return ApiError.fromResponse(error.response?.status ?? 500, body);
  }
  // ...or anything else: a timeout, a dropped connection, a proxy's HTML page.
  // The library's own text ("Request failed with status code 400") explains
  // nothing to a user, so say what the status actually means here.
  const status = error.response?.status ?? 0;
  return ApiError.fromTransport(status, transportMessage(status));
}

function transportMessage(status: number): string | undefined {
  if (status === 400 || status === 431) {
    // Our API always answers a bad request with an error envelope; a bare 400
    // comes from the web server itself, almost always because the request's
    // headers — mostly this site's cookies — have grown too large.
    return 'The request was rejected before it reached the application, usually because this site’s cookies have grown too large. Clear the cookies for this site and try again.';
  }
  if (status === 502 || status === 503 || status === 504) {
    return 'The server is unavailable right now. Try again in a moment.';
  }
  // Undefined keeps the connectivity message for timeouts and dropped connections.
  return undefined;
}

// ------------------------------------------------------------------ verbs

/**
 * Thin wrappers that unwrap the envelope, so callers receive the payload rather
 * than `response.data.data`. `getPage` keeps the pagination metadata, which the
 * table components need.
 */
export const api = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await http.get<ApiResponse<T>>(url, config);
    return response.data.data;
  },

  /**
   * For endpoints whose answer may legitimately be "nothing" — the school has no
   * current academic year, for example.
   *
   * The server omits `data` entirely rather than sending `"data": null`, because
   * the response envelope is serialised with non-null inclusion. That reaches the
   * client as `undefined`, which TanStack Query rejects outright ("Query data
   * cannot be undefined"), turning an ordinary empty result into a query error.
   * Normalising to null here keeps "absent" expressible.
   */
  async getNullable<T>(url: string, config?: AxiosRequestConfig): Promise<T | null> {
    const response = await http.get<ApiResponse<T>>(url, config);
    return response.data.data ?? null;
  },

  async getPage<T>(url: string, config?: AxiosRequestConfig): Promise<PagedResponse<T>> {
    const response = await http.get<PagedResponse<T>>(url, config);
    return response.data;
  },

  async post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await http.post<ApiResponse<T>>(url, body, config);
    return response.data.data;
  },

  async put<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await http.put<ApiResponse<T>>(url, body, config);
    return response.data.data;
  },

  /** Used by the /{id}/status and /set-current endpoints. */
  async patch<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await http.patch<ApiResponse<T>>(url, body, config);
    return response.data.data;
  },

  async delete<T = void>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await http.delete<ApiResponse<T>>(url, config);
    return response.data.data;
  },
};
